/**
 * 🍞 Breadcrumb Provider
 *
 * App'i saran breadcrumb provider. Tüm navigation tracking logic'ini burada handle eder.
 * Breadcrumb component'ları bu provider'dan data alır.
 */

import React, { createContext, useContext, useEffect, useMemo } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  getRouteLabel,
  isPrimaryRoute,
  isSecondaryRoute,
  shouldShowInBreadcrumb,
} from "../config/route-config";
import { useBreadcrumbStore, BreadcrumbItem } from "../store/breadcrumb-store";
import { getModuleHref, useActiveModule } from "@/modules";

// ==================== Context Types ====================

interface BreadcrumbContextValue {
  // Breadcrumb data
  breadcrumbItems: BreadcrumbItem[];
  currentPath: string;

  // Navigation actions
  navigateToItem: (path: string) => void;
  goHome: () => void;

  // Dynamic label management
  setDynamicLabel: (path: string, label: string) => void;

  // Visibility control
  showBreadcrumb: boolean;
  maxVisibleItems: number;
  shouldCollapse: boolean;
  visibleItems: BreadcrumbItem[];
  hiddenItems: BreadcrumbItem[];
}

// ==================== Context Creation ====================

const BreadcrumbContext = createContext<BreadcrumbContextValue | undefined>(
  undefined
);

// ==================== Provider Component ====================

interface BreadcrumbProviderProps {
  children: React.ReactNode;
}

export const BreadcrumbProvider: React.FC<BreadcrumbProviderProps> = ({
  children,
}) => {
  const location = useLocation();
  const navigate = useNavigate();

  // Store hooks
  const {
    history,
    dynamicLabels,
    addToHistory,
    navigateToHistoryItem,
    clearHistory,
    getVisibleHistory,
    setDynamicLabel,
  } = useBreadcrumbStore();

  const currentPath = location.pathname;
  const activeModule = useActiveModule();

  // ==================== Navigation Tracking ====================

  // Track navigation - add current page to history (Smart tracking)
  useEffect(() => {
    // Ana sayfadaysa history'yi temizle
    if (currentPath === "/") {
      clearHistory();
      return;
    }

    // Route config'i kontrol et
    if (!shouldShowInBreadcrumb(currentPath)) {
      return; // Layer route'lar breadcrumb'da gösterilmez
    }

    // PRIMARY route ise history'ye ekle
    if (isPrimaryRoute(currentPath)) {
      const i18nKey = getRouteLabel(currentPath);
      addToHistory({
        path: currentPath,
        i18nKey,
      });
    }

    // SECONDARY route ise history'ye EKLEME! (mevcut context'i koru)
    // Secondary route'lar sadece breadcrumb'da context ile birlikte gösterilir
    // History'yi hiç değiştirmez
  }, [currentPath, addToHistory, clearHistory]);

  // ==================== Breadcrumb Data Processing ====================

  // Get visible breadcrumb items (with home page and dynamic labels)
  const baseItems = useMemo(() => {
    const baseHistory = getVisibleHistory();

    // Dynamic label var mı kontrol et (tüm route'lar için)
    const dynamicLabel =
      dynamicLabels[currentPath] ||
      dynamicLabels["/wizard"] || // Base path fallback
      dynamicLabels["/chat"] || // Chat base path fallback
      Object.values(dynamicLabels).find((_, index) => {
        const key = Object.keys(dynamicLabels)[index];
        return (
          (key?.includes("/wizard") && currentPath.includes("/wizard")) ||
          (key?.includes("/chat") && currentPath.includes("/chat"))
        );
      });

    // Eğer secondary route'daysa (wizard, product vb.)
    if (isSecondaryRoute(currentPath)) {
      // PRIMARY route'lardaki dynamic label'ları koru
      const updatedBaseHistory = baseHistory.map((item) => {
        if (item.path && dynamicLabels[item.path]) {
          return { ...item, dynamicLabel: dynamicLabels[item.path] };
        }
        return item;
      });

      // Current secondary route item'ını oluştur
      const secondaryItem: BreadcrumbItem = {
        path: currentPath,
        i18nKey: getRouteLabel(currentPath),
        timestamp: Date.now(),
      };

      if (dynamicLabel) {
        secondaryItem.dynamicLabel = dynamicLabel;
      }

      // Home + Primary (with dynamic labels) + Current secondary route
      return [
        ...updatedBaseHistory, // Home + Primary route (Chat with dynamic label)
        secondaryItem,
      ];
    }

    // PRIMARY route'lar için dynamic label uygula (mevcut history'deki item'ları güncelle)
    if (isPrimaryRoute(currentPath)) {
      const updatedHistory = baseHistory.map((item) => {
        // Exact match veya base path match (dynamic routes için)
        if (
          item.path === currentPath ||
          (item.path.includes("/chat") && currentPath.includes("/chat")) ||
          (item.path.includes("/wizard") && currentPath.includes("/wizard"))
        ) {
          // Dynamic label varsa uygula
          if (dynamicLabel) {
            return { ...item, dynamicLabel };
          }
          return item;
        }
        return item;
      });
      return updatedHistory;
    }

    return baseHistory;
  }, [history, dynamicLabels, currentPath]);

  // Modül kırıntısı: modül içindeyken Home'dan hemen sonra modül adı gelir
  // (Home › Moda › Alışveriş). Modül kökündeyken zaten history'de olduğu için
  // eklenmez; subdomain modunda kök "/" olur, o zaman Home zaten modüldür.
  const breadcrumbItems = useMemo(() => {
    if (!activeModule) return baseItems;
    const moduleRoot = getModuleHref(activeModule, "");
    if (moduleRoot === "/" || baseItems.some((item) => item.path === moduleRoot)) {
      return baseItems;
    }
    const moduleItem: BreadcrumbItem = {
      path: moduleRoot,
      i18nKey: activeModule.nameKey,
      timestamp: 0,
    };
    return [baseItems[0], moduleItem, ...baseItems.slice(1)];
  }, [baseItems, activeModule]);

  // ==================== Navigation Actions ====================

  const navigateToItem = (path: string) => {
    navigateToHistoryItem(path);
    navigate(path);
  };

  const goHome = () => {
    clearHistory();
    navigate("/");
  };

  // ==================== Visibility Control ====================

  const showBreadcrumb = breadcrumbItems.length > 1;

  // Collapse settings
  const maxVisibleItems = 4; // Home + ... + 2 last items
  const shouldCollapse = breadcrumbItems.length > maxVisibleItems;

  const { visibleItems, hiddenItems } = useMemo(() => {
    if (!shouldCollapse) {
      return {
        visibleItems: breadcrumbItems,
        hiddenItems: [],
      };
    }

    // Show: Home + ... + Last 2 items
    const homeItem = breadcrumbItems[0];
    const lastTwoItems = breadcrumbItems.slice(-2);
    const middleItems = breadcrumbItems.slice(1, -2);

    return {
      visibleItems: [homeItem, ...lastTwoItems],
      hiddenItems: middleItems,
    };
  }, [breadcrumbItems, shouldCollapse]);

  // ==================== Context Value ====================

  const contextValue: BreadcrumbContextValue = {
    // Breadcrumb data
    breadcrumbItems,
    currentPath,

    // Navigation actions
    navigateToItem,
    goHome,

    // Dynamic label management
    setDynamicLabel,

    // Visibility control
    showBreadcrumb,
    maxVisibleItems,
    shouldCollapse,
    visibleItems,
    hiddenItems,
  };

  return (
    <BreadcrumbContext.Provider value={contextValue}>
      {children}
    </BreadcrumbContext.Provider>
  );
};

// ==================== Context Hook ====================

export const useBreadcrumb = (): BreadcrumbContextValue => {
  const context = useContext(BreadcrumbContext);

  if (context === undefined) {
    throw new Error("useBreadcrumb must be used within a BreadcrumbProvider");
  }

  return context;
};

// ==================== Export ====================

export default BreadcrumbProvider;
